// Pure TypeScript functions for matrix operations

import { Matrix, IMatrixDevice, IMatrixResource , IVector2D } from '../types';

/**
 * Parses a raw matrix string into a Matrix object
 *
 * @param rawMatrix - JSON string representation of the matrix
 * @returns Parsed matrix or undefined if parsing fails
 */
export const parseRawMatrix = (rawMatrix?: string): Matrix | undefined => {
  if (typeof rawMatrix !== 'string') {
    return undefined;
  }

  try {
    return JSON.parse(rawMatrix);
  } catch (error) {
    console.warn('Error while parsing raw matrix:', error);
    return undefined;
  }
};

/**
 * Locates a resource ID within the matrix and returns its grid coordinates
 * Returns the last matching occurrence
 *
 * @param resourceId - The resource ID to locate
 * @param matrix - The floor plan matrix
 * @returns Grid coordinates or undefined if not found
 */
export const locateResourceInMatrix = (resourceId: number, matrix?: Matrix): IVector2D | undefined => {
  if (!matrix?.length) {
    return undefined;
  }

  let position: IVector2D | undefined;
  let isDevice = false;

  matrix.forEach((row, rowIndex) => {
    row.forEach((cell: unknown, colIndex: number) => {
      if (typeof cell !== 'object' || cell === null) {
        return;
      }

      // Check if it's a device
      if ('device' in cell) {
        const deviceCell = cell as IMatrixDevice;
        if (deviceCell.device.idResource === resourceId) {
          isDevice = true;
          position = { x: colIndex, y: rowIndex };
        }
      }

      // Check if it's a resource (only if not already found as device)
      if ('resource' in cell && !isDevice) {
        const resourceCell = cell as IMatrixResource;
        if (parseInt(resourceCell.resource.id, 10) === resourceId) {
          position = { x: colIndex, y: rowIndex };
        }
      }
    });
  });

  return position;
};

/**
 * Collects every resource ID present in the matrix (resource cells and device cells) in a single pass.
 * Use it instead of calling locateResourceInMatrix once per resource when only presence matters.
 *
 * @param matrix - The floor plan matrix
 * @returns IDs of the resources that can be located on this matrix
 */
export const getResourceIdsInMatrix = (matrix?: Matrix): Set<number> => {
  const ids = new Set<number>();

  matrix?.forEach((row) => {
    row.forEach((cell: unknown) => {
      if (typeof cell !== 'object' || cell === null) {
        return;
      }

      if ('device' in cell) {
        ids.add((cell as IMatrixDevice).device.idResource);
      }

      if ('resource' in cell) {
        ids.add(parseInt((cell as IMatrixResource).resource.id, 10));
      }
    });
  });

  return ids;
};

/**
 * Gets the cell value at specific coordinates in the matrix
 *
 * @param coords - Grid coordinates
 * @param matrix - The floor plan matrix
 * @returns Cell value or undefined if out of bounds
 */
export const getMatrixCell = (coords: IVector2D, matrix?: Matrix): unknown => {
  if (!matrix?.length) {
    return undefined;
  }

  const row = matrix[coords.y];
  if (!row) {
    return undefined;
  }

  return row[coords.x];
};
